import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, ThemeType } from 'theme/jss-types';
import { User, TableColumn } from './user.types';
import { getUsers, getRoles, downloadUsers } from 'network/user-management';
import {
    Button,
    Input,
    Drawer,
    Select,
    message,
    Tag,
} from 'antd';
import { userColumns } from './user.constants';
import { userManagementStyles } from './user-management.styles';
import {
    EditFilled,
    DownloadOutlined,
    FilterOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ReduxStore } from 'reducers/redux.types';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import AddUser from './AddUser';
import UserDetailsModal from './UserDetailModal';
import { debounce } from 'lodash';
import Helper from '../../library/Helper';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';
import { NAVBAR_HEIGHT } from 'library/globals';
import { Master } from 'types/master-data-types';
import { NextOrPrev } from 'library/Types';
import Paginator from '../common/paginator';

const { Option } = Select;
interface UserManagementProps extends StylesProps<ReturnType<typeof userManagementStyles>> {
    uiTheme: ThemeType,
    master: Master,
}

const UserManagement: React.FC<UserManagementProps> = (props) => {
    const {
        classes, uiTheme, master,
    } = props;
    const { t } = useTranslation();
    const bannerHeight = (master?.config?.customer_portal_config?.password_policy_banner_expiry_date
        && new Date(master?.config?.customer_portal_config?.password_policy_banner_expiry_date) <= new Date()) ? 0 : 50;
    const [users, setUsers] = React.useState<User[]>([]);
    const [searchText, setSearchText] = React.useState<string>('');
    const [isAddUserModalVisible, setIsAddUserModalVisible] = React.useState<boolean>(false);
    const [isFilterModalVisible, setIsFilterModalVisible] = React.useState<boolean>(false);
    const [selectedCity, setSelectedCity] = React.useState<string | undefined>(undefined);
    const [selectedStatus, setSelectedStatus] = React.useState<string | undefined>(undefined);
    const [selectedPhone, setSelectedPhone] = React.useState<string | undefined>(undefined);
    const [editData, setEditData] = React.useState<User | null>(null);
    const [selectedUser, setSelectedUser] = React.useState<User | null>(null); // State for selected user
    const [isUserDetailsModalVisible, setIsUserDetailsModalVisible] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [userRoleList, setUserRoleList] = React.useState<{ id: string; value: string }[]>([]);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [isNextPage, setIsNextPage] = React.useState<boolean>(false);
    // const [rolesList, setRolesList] = React.useState<{ id: string; value: string }[]>([]);
    const [dataChanged, setDataChanged] = React.useState<boolean>(false);
    const [downloading, setDownloading] = React.useState<boolean>(false);
    const [userPermissionsList, setUserPermissionsList] = React.useState<{ [key: string]: string[] }>({});
    // const pageSize = 20;

    const fetchData = async (params = {}) => {
        setLoading(true);
        const usersData = await getUsers(params); // { username: '' }
        if (!usersData.isSuccess) {
            message.error(usersData.errorMessage);
        } else {
            setUsers(usersData?.data?.customerUsers || []);
            if (params?.pageNum) {
                setCurrentPage(Number(params?.pageNum));
            } else {
                setCurrentPage(1);
            }
            // setCurrentPage(usersData?.data?.page);
            setIsNextPage(usersData?.data.isNextPageExists || false);
        }
        setLoading(false);
    };

    React.useEffect(() => {
        fetchData();
    }, [dataChanged]); // Include any dependencies that should trigger a re-fetch
    interface UserRole {
        id: string;
        value: string;
    }
    const handleDownload = async () => {
        setDownloading(true);
        const response = await downloadUsers();
        if (response.isSuccess) {
            Helper.downloadFileData(
                response.data,
                'UserDetails.csv',
                true,
            );
            message.success('Downloaded Successfully');
        } else {
            message.error(response.errorMessage);
        }
        setDownloading(false);
    };
    const fetchUserRolesedit = async (userId: number) => {
        const rolesData = await getRoles(userId);
        if (rolesData.isSuccess) {
            const roles_List = rolesData.data.userRoleGroupArr[0].rolesList || [];
            const userRoles: UserRole[] = roles_List.map((role: { role_id: string; role_name: string }) => ({
                id: role.role_id,
                value: role.role_name,
            }));
            setUserRoleList(userRoles);
        } else {
            message.error(rolesData.errorMessage);
        }
        const usersData = await getUsers(); ///
        if (usersData.isSuccess) {
            // setRolesList(usersData?.data?.userRoleList || []);
            setUserPermissionsList(usersData?.data?.userPermssionList || []);
        }
        setLoading(false);
    };
    const handleFilterApply = async () => {
        const filters: Record<string, string | undefined> = {
            city: selectedCity?.toUpperCase(),
            username: searchText,
            status: selectedStatus,
            pageNum: currentPage.toString(),
            phoneNum: selectedPhone,
        };
        // Filter out undefined or empty values
        const filteredFilters = Object.fromEntries(
            Object.entries(filters).filter(([, value]) => value !== undefined && value !== ''),
        );
        // Check if any of the filters (except pageNum) is not empty
        const {
            city,
            username,
            status,
            phoneNum,
        } = filteredFilters;
        const hasOtherFilters = [city, username, status, phoneNum].some((filter) => filter !== undefined && filter !== '');
        // If there are other filters, set pageNum to '1'
        if (hasOtherFilters) {
            filteredFilters.pageNum = '1';
            setCurrentPage(1);
        }
        // Call fetchData with the filtered filters
        await fetchData(filteredFilters);
        setIsFilterModalVisible(false);
    };
    React.useEffect(() => {
        handleFilterApply();
    }, [currentPage]);

    const handlePagination = (nextOrPrev: NextOrPrev) => {
        let newPageNumber = currentPage;
        if (nextOrPrev === 'first') {
            newPageNumber = 1;
        } else if (nextOrPrev === 'next') {
            newPageNumber = currentPage + 1;
        } else {
            newPageNumber = currentPage - 1;
        }
        setCurrentPage(newPageNumber);
    };

    const renderPaginator = () => {
        return (
            <div style={{ direction: 'ltr' }}>
                <Paginator
                    currentPageNumber={currentPage}
                    isNextPresent={isNextPage}
                    onNextClick={() => handlePagination('next')}
                    onPrevClick={() => handlePagination('prev')}
                    onFirstClick={() => handlePagination('first')}
                />
            </div>
        );
    };

    const debouncedFilterApply = debounce(async () => {
        await handleFilterApply();
    }, 1000);
    React.useEffect(() => {
        debouncedFilterApply();
        // Cleanup the debounce function on unmount
        return () => {
            debouncedFilterApply.cancel();
        };
    }, [searchText]);

    const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setSearchText(value);
        await debouncedFilterApply();
    };

    const renderUserCodeSearch = () => {
        return (
            <Input
                placeholder={t('search_user_code')}
                value={searchText}
                onChange={handleInputChange}
                className={classes.search}
            />
        );
    };

    const renderMoreFiltersButton = () => {
        return (
            <Button
                type="primary"
                onClick={() => setIsFilterModalVisible(true)}
                icon={<FilterOutlined />}
                style={{
                    background: '#ffffff',
                    boxShadow: 'none',
                    color: '#111111',
                    fontWeight: 500,
                    fontSize: 12,
                    borderColor: 'white',
                    padding: '5px 0px',
                }}
            >
                {t('more_filters')}
            </Button>
        );
    };

    const renderDownloadBtn = () => {
        return (
            <Button
                type="primary"
                loading={downloading}
                onClick={handleDownload}
                icon={<DownloadOutlined />}
                style={{
                    marginRight: 12,
                    borderRadius: 4,
                }}
            >
                {t('download')}
            </Button>
        );
    };

    const renderAddUserBtn = () => {
        return (
            <Button
                type="primary"
                onClick={async () => {
                    setEditData(null);
                    setIsAddUserModalVisible(true);
                }}
                icon={<PlusOutlined />}
                style={{
                    borderRadius: 4,
                }}
            >
                {t('Add User')}
            </Button>
        );
    };

    const renderFooter = () => {
        return (
            <div className={classes.footer}>
                {renderPaginator()}
            </div>
        );
    };

    const renderLeftFilters = () => {
        return (
            <div className={classes.leftFilters}>
                {renderUserCodeSearch()}
                {renderMoreFiltersButton()}
            </div>
        );
    };

    const renderRightFilters = () => {
        return (
            <div className={classes.rightFilters}>
                {renderDownloadBtn()}
                {renderAddUserBtn()}
            </div>
        );
    };

    const renderExtraFilters = () => {
        return (
            <div className={classes.extraFilters}>
                {renderLeftFilters()}
                {renderRightFilters()}
            </div>
        );
    };

    const renderFilters = () => {
        return (
            <div className={classes.filters}>
                {renderExtraFilters()}
            </div>
        );
    };

    const renderText = (text: string | number) => {
        return (
            <div className={classes.cellValue}>
                {text}
            </div>
        );
    };

    const handleEditUser = async (user: User) => {
        setEditData(user);
        await fetchUserRolesedit(user.id); // Fetch user roles for edit user
        setIsAddUserModalVisible(true);
    };

    const renderActions = (row: User) => {
        return (
            <>
                <EditFilled
                    key={row.id}
                    style={{
                        color: uiTheme.primaryColor,
                        cursor: 'pointer',
                        marginRight: '10px',
                    }}
                    onClick={() => handleEditUser(row)}
                />
            </>
        );
    };

    const renderColumn = (
        text: string,
        row: User,
        column: string,
        index: number,
    ) => {
        switch (column) {
            case 'id':
                return renderText(index + 1); // (page - 1) * paginationSize + index + 1 set pagination size
            case 'Action':
                return renderActions(row);
            case 'user_code':
                return (
                    <div
                        className={classes.cellValue}
                        style={{ color: '#082E78', cursor: 'pointer' }}
                        onClick={() => {
                            setSelectedUser(row); // Set selected user
                            setIsUserDetailsModalVisible(true); // Open modal
                        }}
                    >
                        {text}
                    </div>
                );
            case 'is_active':
                return (
                    <Tag
                        color={row.is_active ? 'green' : 'red'}
                        style={{
                            borderRadius: '20px',
                        }}
                    >
                        {row.is_active ? ' Active ' : 'Inactive'}
                    </Tag>
                );
            case 'username':
                return renderText(row.username);
            case 'name':
                return renderText(row.name);
            case 'phone':
                return renderText(row.phone);
            case 'city':
                return renderText(row.city);
            case 'created_at':
                return renderText(new Date(row.created_at).toLocaleString());
            case 'updated_at':
                return renderText(new Date(row.updated_at).toLocaleString());
            default:
                return renderText(row.is_active ? 'Active' : 'Inactive');
        }
    };

    const renderColumns = () => {
        const columns: any = userColumns.map((column: TableColumn) => {
            let columnWidth = 120;

            if (column.key === 'id') {
                columnWidth = 80;
            } else if (column.key === 'user_code') {
                columnWidth = 200;
            }
            return {
                key: column.key,
                title: t(column.pretty_name),
                dataIndex: column.key,
                ellipsis: true,
                bordered: false,
                width: columnWidth,
                render: (text: string, row: any, idx: number) => renderColumn(text, row, column.key, idx),
            };
        });
        return columns;
    };

    const handleAddUserSubmit = () => {
        // Add or update user logic
        // const newUser = { ...values, status: values.is_active ? 'Active' : 'Inactive' };
        // if (editData) {
        //     const updatedUsers = users.map((user) => (user.id === editData.id ? { ...user, ...newUser } : user));
        //     setUsers(updatedUsers);
        // } else {
        //     setUsers([...users, { ...newUser, id: users.length + 1 }]);
        // }
        setIsAddUserModalVisible(false);
        fetchData();
        setEditData(null); // Reset edit data
        setDataChanged(true);
    };

    const handleAddUserCancel = () => {
        setIsAddUserModalVisible(false);
        setEditData(null); // Reset edit data
    };
    const handleFilterCancel = () => {
        setIsFilterModalVisible(false);
    };

    const renderTable = () => {
        return (
            <FixedHeaderTable
                columns={renderColumns()}
                className={classes.table}
                dataSource={users}
                pagination={false}
                rowKey={(row) => row.id.toString()}
                scroll={{ y: 650 }}
                loading={loading}
                style={{
                    height: `calc(
                        (((100vh - ${NAVBAR_HEIGHT}px) - 55px) - ${bannerHeight}px) )`,
                    background: '#ffffff',
                }}
            />
        );
    };

    const renderCitySearch = () => {
        return (
            <div style={{ marginBottom: 16 }}>
                <label>{t('City')}</label>
                <Input
                    placeholder={t('Enter City')}
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    style={{ width: '100%' }}
                />
            </div>
        );
    };

    const renderNumberSearch = () => {
        return (
            <div style={{ marginBottom: 16 }}>
                <label>{t('Phone Number')}</label>
                <Input
                    placeholder={t('Enter 10 Digit Phone Number')}
                    value={selectedPhone}
                    onChange={(e) => setSelectedPhone(e.target.value)}
                    style={{ width: '100%' }}
                />
            </div>
        );
    };

    const renderStatusFilter = () => {
        return (
            <div style={{ marginBottom: 16 }}>
                <label>{t('Status')}</label>
                <Select
                    placeholder={t('Select Status')}
                    value={selectedStatus}
                    onChange={(value) => setSelectedStatus(value)}
                    style={{ width: '100%' }}
                >
                    <Option value="true">{t('Active')}</Option>
                    <Option value="false">{t('Inactive')}</Option>
                </Select>
            </div>
        );
    };

    const renderMoreFilters = () => {
        return (
            <div>
                {renderCitySearch()}
                {renderNumberSearch()}
                {renderStatusFilter()}
            </div>
        );
    };

    return (
        <div className={classes.main}>
            {renderFilters()}
            <>
                {renderTable()}
                {renderFooter()}
            </>
            {isAddUserModalVisible
            && (
                <Drawer
                    title={editData ? t('Edit User') : t('Add User')}
                    visible={isAddUserModalVisible}
                    width="40%"
                    onClose={handleAddUserCancel}
                    footer={null}
                    className={classes.drawerMain}
                >
                    <AddUser
                        editData={editData}
                        userRoleList={userRoleList}
                        userPermissionsList={userPermissionsList}
                        onSubmit={handleAddUserSubmit}
                        onCancel={handleAddUserCancel}
                    />
                </Drawer>
            )}
            <Drawer
                title={t('Filter Users')}
                visible={isFilterModalVisible}
                onClose={handleFilterCancel}
                width="30%"
                footer={[
                    <Button key="apply" type="primary" onClick={handleFilterApply}>
                        {t('Apply')}
                    </Button>,
                ]}
                className={classes.drawerMain}
            >
                {renderMoreFilters()}
            </Drawer>
            <UserDetailsModal
                user={selectedUser}
                visible={isUserDetailsModalVisible}
                onClose={() => setIsUserDetailsModalVisible(false)}
                onEdit={() => {
                    setEditData(selectedUser);
                    setIsAddUserModalVisible(true);
                }}
            />
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master, uiTheme } = state;
    return {
        uiTheme,
        master,
    };
};

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

export default withStyles(userManagementStyles)(GenericHoc(hocConfig)(UserManagement));
